import React, { useEffect, useState } from 'react'
import { ConfigProvider, Form, Input, InputNumber, message, Modal, Radio, TreeSelect } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { BackButton, BasicSettingWrapper, RoundTag, SettingWrapper, SubTitle } from '../segment.styled'
import { fetchTaxonomyTree } from '../../../apis/taxonomy'
import SegmentSetting from './SegmentSetting'
import { Sources } from '../../../constants/Sources'
import { addSegment, modSegment } from '../../../apis/segment'
import { useSelector } from 'react-redux'
import CriteriaDetail from '../Criteria/CriteriaDetail'

const initSetting = { profile: [], 1: { include: {}, exclude: {} } }
function SegmentForm (props) {
  const { goBackPath, addMode, editMode } = props
  const { t: ln_t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location || {}
  const { data, goBackPath: goBackFromLocation, fromInvoice } = state || {}
  const user = useSelector((state) => state.user)
  const { nowBrand, nowGroup } = user || {}
  const [tags, setTags] = useState([])
  const [ready, setReady] = useState(false)
  const [setting, setSetting] = useState({ ...initSetting })
  const [sources, setSources] = useState([])
  const [modalData, setModalData] = useState({})
  const [showModal, setShowModal] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)

  const goBack = () => navigate(goBackFromLocation || goBackPath, { state: { type: 'segment' } })
  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`segment.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({
    name, label: ln_t(`segment.${name}`), rules: [{ required, message: defPlaceholder(name, type) }]
  })

  const constructTag = ({ id, name, children = [], parentId }) => ({ value: `${id}-${name}`, label: name, children: children?.map(i => constructTag(i)), checkable: !children.length })
  const initialFetch = async () => {
    const tag = await fetchTaxonomyTree(nowGroup, { status: 1 })
    if (tag?.status === 200) setTags(tag?.data?.map(i => constructTag(i)))
  }

  const handleAdd = async reqBody => {
    setSaveLoading(true)
    const res = await addSegment(reqBody)
    if (res?.status === 202) { message.success(ln_t('message.success.create')); goBack() }
    setSaveLoading(false)
  }
  const handleEdit = async (id, reqBody) => {
    setSaveLoading(true)
    const res = await modSegment(id, { ...data, ...reqBody })
    if (res?.status === 200) { message.success(ln_t('message.success.modify')); goBack() }
    setSaveLoading(false)
  }

  const getSrcId = name => sources.find(i => i.name === name)?.id
  const formatRule = data => Object.values(data).flat().map(({ end, id, type }) => end ? ({ type: getSrcId(type), id }) : undefined)?.filter(i => i)
  const handleSave = value => {
    const { category, maxCount, ...other } = value
    const { profile, ...segments } = setting || {}
    const dataInSegments = Object.values(segments).map(mode => Object.values(mode).map(type => Object.values(type)?.flat())?.flat()).flat() || []
    if (profile?.length === 0 && dataInSegments?.length === 0) return message.error(ln_t('message.error.noRules'))
    if (profile.length === 1) return message.error(ln_t('message.error.profileLessThanTwo'))
    const interestError = []
    const checkInterest = (data, name, key) => data?.[Sources.INTEREST]?.length < 2 ? interestError.push(`${ln_t('segment.segment')} #${key}  ${ln_t(`segment.${name}`)}`) : ''

    const clearEmptyInEx = item => Object.entries(item).filter(([key, val]) => Object.keys(val)?.length).reduce((all, [key, val]) => ({ ...all, [key]: val }), {})
    const formatSegment = Object.values(segments)
      .map(({ include, exclude }) => ({ include: clearEmptyInEx(include), exclude: clearEmptyInEx(exclude) }))
      .filter(({ include, exclude }) => Object.keys(include)?.length || Object.keys(exclude)?.length)
      .reduce((all, value, idx) => ({ ...all, [`${idx + 1}`]: value }), {})
    if (Object.keys(segments)?.length !== Object.keys(formatSegment)?.length) return message.error(ln_t('message.error.criteriaCantBeEmpty'))

    const rules = [
      ...Object.keys(formatSegment).map(key => {
        const { include = {}, exclude = {} } = segments[key] || {}
        checkInterest(include, 'include', key); checkInterest(exclude, 'exclude', key)
        return { include: formatRule(include), exclude: formatRule(exclude) }
      }),
      ...profile && profile.length ? [{ include: formatRule(profile) }] : []
    ]
    const UISetting = { profile, ...formatSegment, category }

    if (interestError.length) return message.error(`${ln_t('message.error.interestLessThanTwo')} (${interestError.join(', ')})`)
    const reqBody = { ...other, brandId: nowBrand, maxCount: maxCount || 0, taxonomyId: category?.map(i => i.split('-')[0]).join(','), UISetting, rules }
    // if (reqBody) return console.log(reqBody)
    addMode ? handleAdd(reqBody) : handleEdit(data?.id, reqBody)
  }

  useEffect(() => {
    if ((!addMode && !editMode) || (editMode && !data)) return goBack()
    initialFetch()
    if ((addMode && !data) || fromInvoice) return setReady(true)
    const { name, maxCount, UISetting } = data || {}
    const { category, ...otherSetting } = UISetting || {}
    form.setFieldsValue({
      ...data,
      name: editMode ? name : `${data.name} copy`,
      maxCount: maxCount !== 0 ? maxCount : undefined,
      category
    })
    setSetting(otherSetting)
    setReady(true)
  }, [])

  const passProps = { goBack, setting, setSetting, sources, setSources, saveLoading, setShowModal, setModalData }
  return (
    <>
      <BackButton>
        <ArrowLeftOutlined onClick={goBack} />
        {ln_t(`util.${addMode ? 'addNew' : 'edit'}`)} {ln_t('segment.segment')}
      </BackButton>
      <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>

        <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleSave(e)}>
          <SettingWrapper>
            <BasicSettingWrapper>
              <SubTitle>{ln_t('util.basicSetting')}</SubTitle>
              <Form.Item {...defConfig('name', 'input')}>
                  <Input placeholder={defPlaceholder('name', 'input')} />
              </Form.Item>
              <Form.Item {...defConfig('activity', 'input')} name="interval">
                  <InputNumber min={0} max={180} placeholder={defPlaceholder('activity', 'input')} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item {...defConfig('limit', 'input', false)} name="maxCount">
                  <InputNumber min={0} placeholder={defPlaceholder('limit', 'input')} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item {...defConfig('status', 'select')} initialValue={1}>
                <Radio.Group
                  options={[{ value: 1, label: ln_t('util.enable') }, { value: 0, label: ln_t('util.disable') }]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item {...defConfig('category', 'select', false)}>
                <TreeSelect
                  placeholder={defPlaceholder('category', 'select')}
                  treeCheckable="multiple"
                  treeData={tags}
                />
              </Form.Item>
            </BasicSettingWrapper>
            {ready ? <SegmentSetting {...passProps} /> : ''}
          </SettingWrapper>
        </Form>

        <Modal
          open={showModal}
          footer={null}
          title={<>{modalData?.name} <RoundTag>{modalData?.dataSourceType?.name}</RoundTag></>}
          onCancel={() => setShowModal(false)}
        >
          <CriteriaDetail modalMode modalData={modalData} />
        </Modal>
      </ConfigProvider>
    </>
  )
}

export default SegmentForm
